// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchMessagingSummary } from '../services/api';
import { useMessageReadState, useUnreadMessagingSummary } from '../utils/messageReadState';

const S = {
  container: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 9999,
    width: 'min(380px, calc(100vw - 32px))',
    background: '#ffffff',
    border: '2px solid #3b82f6',
    borderRadius: 16,
    boxShadow: '0 12px 36px rgba(15, 23, 42, 0.22)',
    padding: '16px 18px',
    animation: 'aeSlideUp 0.3s ease-out',
    fontFamily: 'inherit',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  titleArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  iconBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: '#eff6ff',
    color: '#2563eb',
    fontSize: 16,
    border: '1px solid #bfdbfe',
  },
  title: {
    fontSize: 14,
    fontWeight: 800,
    color: '#1e3a5f',
    margin: 0,
    lineHeight: 1.3,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: 18,
    fontWeight: 700,
    padding: '0 4px',
    lineHeight: 1,
    borderRadius: 4,
  },
  studentBadge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 700,
    color: '#0369a1',
    background: '#e0f2fe',
    borderRadius: 6,
    padding: '2px 8px',
    marginBottom: 8,
  },
  bodySnippet: {
    fontSize: 13,
    color: '#334155',
    lineHeight: 1.5,
    margin: '0 0 12px 0',
    maxHeight: 60,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    background: '#f8fafc',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #f1f5f9',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  dismissBtn: {
    background: '#f1f5f9',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  viewBtn: {
    background: '#2563eb',
    color: '#ffffff',
    border: '1px solid #1d4ed8',
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
};

export default function NewMessagePopup({ defaultRole }: any) {
  const auth = useAuth();
  const user = auth.user;
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState<any>(null);
  const userId = user?.user_id ?? user?.id;
  const { dismissedIds, dismissMessage } = useMessageReadState(user);
  const unreadSummary = useUnreadMessagingSummary(summary && summary.userId === userId ? summary.data : null, user);
  const activeMessage = unreadSummary?.incoming_messages.find(
    (message) => !dismissedIds.includes(message.message_id),
  );

  const role = defaultRole || user?.role || 'teacher';
  const rolePrefix = role === 'parent' || role === 'family' ? '/parent' : role === 'student' ? '/student' : '/teacher';

  const isMessagesPage =
    location.pathname.startsWith('/teacher/connect') ||
    location.pathname.startsWith('/teacher/messaging') ||
    location.pathname.startsWith('/parent/connect') ||
    location.pathname.startsWith('/student/connect');

  useEffect(() => {
    let mounted = true;
    let polling = false;
    async function checkNewMessages() {
      if (polling) return;
      polling = true;
      try {
        const data = await fetchMessagingSummary();
        if (mounted) setSummary({ userId, data });
      } catch (error: any) {
        console.warn('Could not check for new messages:', error);
        if (mounted) setSummary(null);
      } finally {
        polling = false;
      }
    }
    checkNewMessages();
    const interval = setInterval(checkNewMessages, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [userId]);

  const handleDismiss = () => {
    if (activeMessage) {
      dismissMessage(activeMessage.message_id);
    }
  };

  const handleView = () => {
    if (activeMessage) {
      const participantId = activeMessage.sender_id;
      const studentId = activeMessage.student_id || '';
      const destPath = rolePrefix === '/parent'
        ? `/parent/connect/messages?participant_id=${participantId}&student_id=${studentId}`
        : `${rolePrefix}/connect?participant_id=${participantId}&student_id=${studentId}`;
      navigate(destPath);
      setSummary(null);
    }
  };

  if (!activeMessage || isMessagesPage) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={S.container}
      className="ae-message-popup"
      data-testid="new-message-popup"
    >
      <div style={S.header}>
        <div style={S.titleArea}>
          <span style={S.iconBadge} aria-hidden="true">💬</span>
          <h4 style={S.title}>New Message from {activeMessage.sender_name}</h4>
        </div>
        <button
          type="button"
          style={S.closeBtn}
          onClick={handleDismiss}
          aria-label="Dismiss message notification"
        >
          ✕
        </button>
      </div>

      {activeMessage.student_name && (
        <span style={S.studentBadge}>
          Regarding {activeMessage.student_name}
        </span>
      )}

      <div style={S.bodySnippet}>
        &ldquo;{activeMessage.body}&rdquo;
      </div>

      <div style={S.footer}>
        <button
          type="button"
          style={S.dismissBtn}
          onClick={handleDismiss}
        >
          Dismiss
        </button>
        <button
          type="button"
          style={S.viewBtn}
          onClick={handleView}
        >
          View Message →
        </button>
      </div>
    </div>
  );
}
